/**
 * C1 v1.3: the Overview tab's Weekly margin moves read (C2 v1.5 `GET /api/margin-weeks/{market}`), its URL codec and
 * every sentence of the section. Types mirror C2's JSON field for field; every percentage stays the API's string and the
 * move is the API's own figure per measure. The only numeric conversions here order the rows (layout) and read the
 * absolute size of a move for that order, never a figure shown as fact.
 *
 * C1 v1.27 (c1-margin-week-threshold, CL-027 second half): the answer carries the Config threshold (`threshold`), the
 * API's verdict per measure on every move (`flagged`) and the action a person recorded on the row's move (`action`); the
 * browser reads the verdict, never judges a move itself, and its one write is `recordMoveAction` (C2 v1.30).
 *
 * C1 v1.31 (c1-base-week-note, item 8): each week carries the marker C2 v1.34 serves (`reconstructed`, `carried_back`), so the
 * week sheet marks a week rebuilt from the store's dated history (c4-margin-backfill) and names what it carried back, and a
 * move that touches such a week says so.
 *
 * C1 v1.62 (c1-margin-move-owner, T12b): C2 v1.59 names the Config owner of flagged moves (`owner`), says per row whether its
 * move still needs an action (`needs_action`), counts those rows (`open_moves`) and labels the margin basis (`grid.margin_basis`).
 * The screen shows them as served and ignores them when an older API omits them. `needs_action` null is no verdict (no
 * threshold, no move, or a move C2 will not judge, such as two weeks costed differently): never read as "no action needed".
 */
import { call, getMarginWeeks, GRID_STALE_TIME, type Market } from "./api";
import type { FeedIssue } from "./feed_issues";
import { countryName, personName, REGIONS, type Region } from "./format";

export type Measure = "gross_pct" | "cogs_fulfilment_pct" | "true_contribution_pct";
export type WeeksCount = 4 | 13 | 26 | 52;
export type Grain = "default" | "all";
export type Scope = "shopify_uk" | "shopify_eu" | "shopify_mena" | "company";
export type Move = { from: string; to: string; gross_pct: string | null; cogs_fulfilment_pct: string | null; true_contribution_pct: string | null };
/** The API's verdict per measure: larger than the threshold (true), within it (false), nothing to judge (null). */
export type Flagged = Record<Measure, boolean | null>;
export type ActionKind = "action" | "none";
/** What a person recorded on the row's move ending on `iso_week`, as C2 v1.30 serves it. */
export type MoveAction = { iso_week: string; from_week: string; measure: Measure; kind: ActionKind; text: string | null; by: string; at: string };
/** The Config weekly-move threshold as the API read it: a decimal string, or null with the sentence to show (D22). */
export type Threshold = { value: string | null; not_set: string | null };
/** C2 v1.59: the Config owner of flagged moves, an email, or null with the sentence to show (never a fallback person). */
export type Owner = { value: string | null; not_set: string | null };
export type WeekPoint = {
  iso_week: string; week_start: string; as_of: string; source_run_id: number; price: string | null; net_price: string | null;
  gross_pct: string | null; cogs_fulfilment_pct: string | null; fulfilment_pct: string | null; fees_pct: string | null;
  true_contribution_pct: string | null; missing: string[];
  /** C2 v1.34: true on a week rebuilt once from the store's dated history (before the first stored grid), with the inputs it
   * carried back from a later date (`fulfilment:2026-09-18`, `fx:2026-09-16`). Absent on an older API: read as a live week. */
  reconstructed?: boolean; carried_back?: string[];
};
export type WeekRow = {
  xero_sku: string; product: string | null; variant: string | null; channel_location: string; channel: "amazon" | "shopify";
  grain: "marketplace" | "store" | "ship_country"; marketplace_id: string; location: string | null; currency: string | null;
  cogs_basis: string; series: WeekPoint[]; latest: string; move: Move | null; flagged: Flagged | null; action: MoveAction | null;
  /** C2 v1.59: true while a flagged move has no recorded action, false when actioned or within the threshold, null for no
   * verdict. Absent on an older API. */
  needs_action?: boolean | null;
};
export type OrderPoint = {
  iso_week: string; week_start: string; orders: number; revenue: string | null; profit: string | null; margin_pct: string | null;
  fx_rate_date: string | null; stores_included: string[]; missing: string[]; computed_run_id: number;
};
export type OrderLine = { scope: Scope; currency: string; series: OrderPoint[] };
export type CurrentWeek = { iso_week: string; week_start: string; days_elapsed: number };
export type MarginWeeks = {
  market: Market; weeks: string[]; current_week: CurrentWeek; grain: Grain; computed_run_id: number | null; threshold: Threshold;
  /** C2 v1.59; absent on an older API. */
  owner?: Owner; open_moves?: number;
  grid: { label: string; basis: string | null; margin_basis?: string; rows: WeekRow[] };
  orders: { label: string; lines: OrderLine[] };
  /** C2 v1.62: the open issues of the margin and order feeds behind these weeks; absent from an older API. */
  feed_issues?: FeedIssue[];
};

export const MEASURES: Measure[] = ["gross_pct", "cogs_fulfilment_pct", "true_contribution_pct"];
export const MEASURE_LABEL: Record<Measure, { short: string; long: string }> = {
  gross_pct: { short: "GM", long: "Gross Margin" },
  cogs_fulfilment_pct: { short: "COGS + Fulfilment", long: "COGS + Fulfilment" },
  true_contribution_pct: { short: "TRUE CM", long: "TRUE Contribution Margin" },
};
export const WEEK_COUNTS: WeeksCount[] = [4, 13, 26, 52];
export const SCOPE_LABEL: Record<Scope, string> = { shopify_uk: "Shopify UK", shopify_eu: "Shopify EU", shopify_mena: "Shopify GCC", company: "Company (GBP)" };
export const SCOPE_TONE: Record<Scope, string> = { shopify_uk: "var(--color-ks-purple-2)", shopify_eu: "var(--color-ks-pass)", shopify_mena: "var(--color-ks-warn)", company: "var(--color-ks-purple)" };

/** The section's region (the Price Run's `pp_region`, so the two tabs share it), weeks, grain and measure, read from the URL. */
export type WeeklyParams = { region: Exclude<Region, "all">; market: Market; weeks: WeeksCount; grain: Grain; measure: Measure };

export function paramsFrom(search: Record<string, unknown>): WeeklyParams {
  // `eu` when the key is absent: the Price Run's own default, so the key the two tabs share never disagrees between them.
  const region = (["uk", "eu", "ae"] as const).includes(search.pp_region as "uk") ? (search.pp_region as "uk" | "eu" | "ae") : "eu";
  const weeksRaw = typeof search.pp_weeks === "string" || typeof search.pp_weeks === "number" ? Number(search.pp_weeks) : 13;
  const weeks = WEEK_COUNTS.includes(weeksRaw as WeeksCount) ? (weeksRaw as WeeksCount) : 13;
  const measure = MEASURES.includes(search.pp_measure as Measure) && search.pp_measure !== "true_contribution_pct" ? (search.pp_measure as Measure) : "gross_pct";
  return { region, market: REGIONS[region].markets[0], weeks, grain: search.pp_grain === "all" ? "all" : "default", measure };
}

/** The prefix of every weekly-series cache entry: a recorded action invalidates them all (every window shows the row). */
export const MARGIN_WEEKS_KEY = ["pricing", "margin-weeks"] as const;
/** One cache entry per market, window and grain; the measure toggle is a view over the same answer. */
export function marginWeeksQuery(p: Pick<WeeklyParams, "market" | "weeks" | "grain">) {
  return { queryKey: [...MARGIN_WEEKS_KEY, p.market, p.weeks, p.grain] as const, queryFn: () => getMarginWeeks<MarginWeeks>(p.market, p.weeks, p.grain), retry: 1, staleTime: GRID_STALE_TIME };
}

// The one write (C2 v1.30 `POST /api/margin-weeks/{market}/actions`): people with the module edit, on a flagged move only.
export type ActionWrite = { xero_sku: string; grain: WeekRow["grain"]; marketplace_id: string; location: string | null; iso_week: string; measure: Measure; kind: ActionKind; text?: string };
export type ActionWritten = { action: MoveAction; move: Move; flagged: Flagged; threshold: Threshold };
export const recordMoveAction = (market: Market, body: ActionWrite) => call<ActionWritten>(`/margin-weeks/${market}/actions`, { method: "POST", body: JSON.stringify(body) });
/** The body for a row's move: the row's own key as the API served it (a marketplace row carries its country), the week the move ends on, the measure on screen. */
export function actionBody(row: WeekRow, measure: Measure, kind: ActionKind, text?: string): ActionWrite {
  return { xero_sku: row.xero_sku, grain: row.grain, marketplace_id: row.marketplace_id, location: row.location, iso_week: row.move?.to ?? row.latest, measure, kind, ...(kind === "action" ? { text } : {}) };
}
/** The API's verdict on the move for one measure; null when there is no threshold or no move. */
export const isFlagged = (row: WeekRow, measure: Measure): boolean | null => row.flagged?.[measure] ?? null;
/** Whether the API flagged the move on any measure: the action is about the move, whichever measure is on screen. */
export const anyFlagged = (row: WeekRow): boolean => MEASURES.some((m) => row.flagged?.[m] === true);
/** The chip's tone: warn for a flagged move that is bad for the measure, pass for a flagged good one, neutral within the threshold or unjudged. */
export function moveTone(row: WeekRow, measure: Measure): "warn" | "pass" | null {
  const move = row.move?.[measure];
  if (move == null || !isFlagged(row, measure)) return null;
  return moveIsBad(move, measure) ? "warn" : "pass";
}

/** The basis badge: C2's `grid.margin_basis` in words, a code this screen does not know as served; null on an older API. */
export function basisLabel(data: MarginWeeks): string | null {
  const basis = data.grid.margin_basis;
  if (basis == null) return null;
  return Object.hasOwn(WEEKLY_COPY.basis, basis) ? WEEKLY_COPY.basis[basis] : basis;
}

/** The size of a move for ordering the list: a layout number, never shown. */
const size = (row: WeekRow, measure: Measure): number => (row.move?.[measure] == null ? -1 : Math.abs(Number(row.move[measure])));
/** Largest move first, then product then variant, as the approved mockup orders the list. A flagged move is larger than the threshold
 * and an unflagged one is not, so this order already places the flagged rows first (the API serves the verdict, not a rank). */
export function orderRows(rows: WeekRow[], measure: Measure): WeekRow[] {
  return [...rows].sort((a, b) => size(b, measure) - size(a, measure) || (a.product ?? a.xero_sku).localeCompare(b.product ?? b.xero_sku) || (a.variant ?? "").localeCompare(b.variant ?? ""));
}
/** Whether a move is bad for the measure: a fall in a margin, a rise in the cost share. Read off the sign of the API's string;
 * it colours a chip only once the API has flagged the move (`moveTone`). */
export function moveIsBad(move: string, measure: Measure): boolean {
  return measure === "cogs_fulfilment_pct" ? !move.startsWith("-") && move !== "0.0" : move.startsWith("-");
}
/** "+2.4 pts", "−1.8 pts", "0.0 pts": the sign is read off the string, the digits are the API's. */
export function movePts(move: string): string {
  return `${move.startsWith("-") ? "−" : move === "0.0" ? "" : "+"}${move.replace(/^-/, "")} pts`;
}
export function rowLabel(row: WeekRow): string {
  const product = row.product ? `${row.product}${row.variant ? ` ${row.variant}` : ""}` : row.xero_sku;
  const channel = CHANNEL_LABEL[row.channel_location] ?? row.channel_location;
  return `${row.grain === "ship_country" && row.location ? `${channel} · ${countryName(row.location)}` : channel} · ${product}`;
}
export const CHANNEL_LABEL: Record<string, string> = {
  amazon_uk: "Amazon UK", amazon_eu: "Amazon EU", amazon_ae: "Amazon UAE", shopify_uk: "Shopify UK", shopify_eu: "Shopify EU", shopify_mena: "Shopify GCC",
};
export function rowKey(row: WeekRow): string {
  return `${row.xero_sku}|${row.grain}|${row.marketplace_id}|${row.location ?? ""}`;
}
/** The Monday of any week in the window, stepped back by whole weeks from the API's `current_week` (a date step, never a figure), so the
 * summary line reads as dates even for a gap week no row carries a point for. */
export function weekMonday(current: CurrentWeek, weeks: string[], isoWeek: string): string {
  const back = weeks.indexOf(current.iso_week) - weeks.indexOf(isoWeek);
  const monday = new Date(`${current.week_start}T00:00:00Z`);
  monday.setUTCDate(monday.getUTCDate() - 7 * back);
  return monday.toISOString().slice(0, 10);
}
/**
 * The weeks the store lines are drawn over: the window up to the last week any line carries a point for. Weeks after it (the
 * per-order feed has not reached them yet) are left off the axis and named in `trailing`, so an axis never runs past the data
 * with nothing to say why (bug 4020402f). Gaps inside the window stay on the axis: a line breaks there, never bridged.
 */
export function storeLineWeeks(weeks: string[], lines: OrderLine[]): { shown: string[]; trailing: string[] } {
  const carried = new Set(lines.flatMap((l) => l.series.map((p) => p.iso_week)));
  let last = -1;
  weeks.forEach((w, i) => { if (carried.has(w)) last = i; });
  if (last < 0) return { shown: weeks, trailing: [] };
  return { shown: weeks.slice(0, last + 1), trailing: weeks.slice(last + 1) };
}

/** The newest `as_of` served on any row, or null: the date the Calculated line names. */
export function newestAsOf(rows: WeekRow[]): string | null {
  return rows.flatMap((r) => r.series.map((p) => p.as_of)).sort().at(-1) ?? null;
}
/** "3 Mar 2026" from an ISO date, UTC so it never shifts a day. */
const DATE = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
export const weekDate = (iso: string) => DATE.format(new Date(`${iso}T00:00:00Z`));
/** The inputs a rebuilt week can carry back (C2's codes `<kind>:<date>`), as the screen names them. */
const CARRIED_KIND: Record<string, string> = { fulfilment: "fulfilment fees", fx: "ECB rates" };
/** A whole code as C2 writes it, `<kind>:<YYYY-MM-DD>`. */
const CODE = /^([^:]+):(\d{4}-\d{2}-\d{2})$/;
/** A day that is on the calendar: `2026-02-30` is not (the Date parser would roll it over to 2 March). */
const realDay = (day: string) => {
  const at = new Date(`${day}T00:00:00Z`);
  return !Number.isNaN(at.getTime()) && at.toISOString().slice(0, 10) === day;
};
/** "fulfilment fees (10 Sept 2026, 18 Sept 2026), ECB rates (16 Sept 2026)": C2's codes grouped by kind in the order served,
 * each date as given; a code of a kind not named here (an inherited object key such as `constructor` included), or one that is
 * not a whole `<kind>:<YYYY-MM-DD>` with a real day, shows as served: never dropped, and never handed to the date format, which
 * throws on an invalid date. Empty for no code. */
export function carriedBackText(codes: string[]): string {
  const days = new Map<string, string[]>();
  const other: string[] = [];
  for (const code of codes) {
    const [, kind, day] = CODE.exec(code) ?? [];
    if (kind && day && Object.hasOwn(CARRIED_KIND, kind) && realDay(day)) days.set(kind, [...(days.get(kind) ?? []), weekDate(day)]);
    else other.push(code);
  }
  return [...[...days].map(([kind, list]) => `${CARRIED_KIND[kind]} (${list.join(", ")})`), ...other].join(", ");
}
/** A week the API marks rebuilt; a week from an older API is live. */
export const isRebuilt = (point: WeekPoint | null | undefined) => point?.reconstructed === true;
/** The row's rebuilt weeks ("2026-W30", or "2026-W30 to 2026-W37" for a span) and every code they carried back, each once
 * and sorted; null when no week of the row is rebuilt. */
export function rebuiltSpan(row: WeekRow): { weeks: string; carried: string[] } | null {
  const points = row.series.filter(isRebuilt);
  if (points.length === 0) return null;
  const first = points[0].iso_week, last = points[points.length - 1].iso_week;
  return { weeks: first === last ? first : `${first} to ${last}`, carried: [...new Set(points.flatMap((p) => p.carried_back ?? []))].sort() };
}

export const WEEKLY_COPY = {
  // Sections 1 and 2 are live from C1 v1.9 (kpi.ts, MarginHealthSection, HighlightsSection); the two release cards remain.
  sections: [
    { n: 3, title: "Market position", desc: "Our price against the competitor basket.", later: "Arrives once k371 Competitive Position has a calculation." },
    { n: 4, title: "Reprice signals", desc: "Costs that have already moved and the date to reprice by.", later: "Arrives once Future COGS is built." },
  ],
  pageTitle: "Margin health across markets",
  pageNote: "Where attention is needed first. One click into a region's Price Run.",
  title: "Weekly margin moves",
  desc: "Week on week, and what was done about it.",
  measure: "Measure",
  region: "Region",
  weeks: "Weeks",
  showCountries: "Show ship countries",
  trueCmOff: "Not yet: no discount, refund or ads feed.",
  threshold: (t: Threshold, flagged: number) => (t.value === null ? "threshold not set (Config): no move is flagged, no action is recorded" : `threshold ${t.value} pts (set in Config) · ${flagged} flagged`),
  summary: (measure: string, from: string, to: string, rows: number, threshold: string) => `${measure} as % of revenue, week of ${from} vs ${to} · ${threshold} · ${rows} rows`,
  summaryToDate: (measure: string, from: string, to: string, days: number, rows: number, threshold: string) => `${measure} as % of revenue, week of ${from} vs ${to} to date (${days} of 7 days) · ${threshold} · ${rows} rows`,
  toDate: "to date",
  oneWeek: "one week",
  // C1 v1.62: the owner line, C2's count of moves still needing an action, the row highlight and the basis badge.
  owner: (o: Owner) => (o.value === null ? (o.not_set ?? "Owner not set in Config") : `Owner of flagged moves: ${personName(o.value)} (${o.value}, set in Config)`),
  openMoves: (n: number) => (n === 0 ? "no move needs an action" : n === 1 ? "1 move needs an action" : `${n} moves need an action`),
  needsAction: "Needs action",
  noVerdict: "no verdict",
  basis: { standard: "Standard margin", order_actuals: "Order actuals" } as Record<string, string>,
  action: {
    within: "within threshold",
    record: "Record action",
    none: "No action needed",
    prompt: "What are you doing about it?",
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    change: "Change",
    recorded: (text: string, who: string, when: string) => `Action: ${text} (${who}, ${when})`,
    noneRecorded: (who: string, when: string) => `No action needed · ${who}, ${when}`,
  },
  calculated: (stamp: string, run: number) => `Calculated · ${stamp} · run ${run}`,
  linesTitle: "Store and company margin per week",
  linesNote: "From the per-order actuals: profit over net revenue of the costed orders, per store in the store currency. Not affected by the region toggle.",
  linesLegend: "Company line: GBP at the stored ECB rate, module-computed",
  noWeeks: "no weeks",
  noLines: "No store or company weeks in this window.",
  linesAsOf: (week: string, monday: string) => `Orders to ${week} (week of ${monday})`,
  linesTrailing: (from: string, to: string) =>
    `${from === to ? from : `${from} to ${to}`} not drawn: no orders read for ${from === to ? "it" : "them"}. If the order feed is late, the note at the top of the page says so.`,
  gccNotConverted: "Shopify GCC not converted: no ECB dirham rate",
  empty: "No weekly series yet. The daily calculation writes one row per SKU and channel per week.",
  firstWeek: "First week recorded. Moves appear next week.",
  noRows: (region: string, weeks: number) => `No weeks for ${region} in the last ${weeks} weeks.`,
  footnote: "Amazon rows are the standard margin at the marketplace price live in the week; Shopify store rows are the order-weighted standard margin across ship countries, as the Price Run shows them. A week marked Rebuilt in the row's sheet was rebuilt once from the store's dated history, before the first stored grid; weeks before those have no rows. Nothing here is a KPI: the KPI system stays the source of every actual.",
  rebuiltMove: "includes a rebuilt week",
  rebuiltWeek: "a rebuilt week",
  sheet: {
    subtitle: (currency: string | null, basis: string) => `Standard margins per ISO week · ${currency ?? "no currency"} · ${basis}`,
    columns: ["Week", "As of", "Price", "Net price", "Gross", "COGS + fulfilment", "Fulfilment", "Fees", "Missing"],
    trueCm: "TRUE Contribution Margin: not yet (no discount, refund or ads feed).",
    source: "Standard margins from the grid run of each week.",
    rebuilt: "Rebuilt",
    rebuiltNote: (weeks: string, carried: string) =>
      `Rebuilt ${weeks}: from the store's dated history, not from a grid run.${carried ? ` Where the store held nothing yet for a rebuilt week, the first value stored was used: ${carried}.` : ""}`,
    openPriceRun: "Open in Price Run",
  },
} as const;
